import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fadeInUpVariants, easings } from '../../hooks/useScrollAnimation';
import {
  SiReact,
  SiTypescript,
  SiNodedotjs,
  SiNextdotjs,
  SiFirebase,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiAndroid,
  SiApple,
} from 'react-icons/si';
import { TbBrandReactNative } from 'react-icons/tb';
import { IconType } from 'react-icons';

const SkillsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.spacing.xl};
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.lg};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing.xxl};
  }
`;

const Kicker = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.display};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 600px;
  line-height: 1.6;
`;

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const SkillCard = styled(motion.div)<{ $highlight?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme, $highlight }) => ($highlight ? theme.colors.card : theme.colors.surface)};
  border: 2px solid
    ${({ theme, $highlight }) => ($highlight ? theme.colors.primary : theme.colors.border)};
  position: relative;
  transition:
    border-color 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.card};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const SkillIcon = styled.div`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const SkillName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-family: ${({ theme }) => theme.fonts.display};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const SkillLevel = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

interface Skill {
  name: string;
  icon: IconType;
  level: string;
  highlight?: boolean;
}

const skillsData: Skill[] = [
  { name: 'React Native', icon: TbBrandReactNative, level: 'Expert', highlight: true },
  { name: 'React', icon: SiReact, level: 'Expert' },
  { name: 'TypeScript', icon: SiTypescript, level: 'Advanced' },
  { name: 'iOS', icon: SiApple, level: 'Advanced' },
  { name: 'Android', icon: SiAndroid, level: 'Advanced' },
  { name: 'Next.js', icon: SiNextdotjs, level: 'Intermediate' },
  { name: 'Node.js', icon: SiNodedotjs, level: 'Advanced' },
  { name: 'Firebase', icon: SiFirebase, level: 'Advanced' },
  { name: 'MongoDB', icon: SiMongodb, level: 'Intermediate' },
  { name: 'PostgreSQL', icon: SiPostgresql, level: 'Intermediate' },
  { name: 'Docker', icon: SiDocker, level: 'Intermediate' },
];

const Skills: React.FC = () => {
  return (
    <SkillsSection id="skills">
      <SectionHeader>
        <Kicker>What I Do</Kicker>
        <SectionTitle
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUpVariants}
        >
          Skills & Expertise
        </SectionTitle>
        <SectionDescription
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUpVariants}
        >
          Building scalable mobile and web applications with modern technologies
        </SectionDescription>
      </SectionHeader>

      <SkillsGrid
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.08,
              delayChildren: 0.1,
              ease: [0.4, 0, 0.2, 1],
            },
          },
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
      >
        {skillsData.map((skill, _index) => (
          <SkillCard
            key={skill.name}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: easings.easeOutQuint,
                },
              },
            }}
            transition={{
              duration: 0.5,
              ease: easings.easeOutQuint,
            }}
            whileHover={{ scale: 1.02, y: -4 }}
            whileTap={{ scale: 0.98 }}
            $highlight={skill.highlight}
          >
            <SkillIcon>
              <skill.icon />
            </SkillIcon>
            <SkillName>{skill.name}</SkillName>
            <SkillLevel>{skill.level}</SkillLevel>
          </SkillCard>
        ))}
      </SkillsGrid>
    </SkillsSection>
  );
};

export default Skills;
